import React from 'react';
import { Trash2, Check, Lightbulb, Gavel } from 'lucide-react';
import { CraftBlock } from '../../types';
import { t } from '../../lib/i18n';

interface CraftBlockEditorProps {
  block: CraftBlock;
  isDarkMode: boolean;
  onChange: (id: string, content: string) => void;
  onToggleChecked: (id: string) => void;
  onDelete: (id: string) => void;
}

/**
 * Tek bir Craft bloğunu düzenlenebilir olarak çizer.
 *
 * Ayrı bir dosyaya alınmasının sebebi yalnızca düzen değil: eski hâlinde
 * CraftNoteView içinde yalnızca dört tip (heading, callout, quote, paragraph)
 * ele alınıyordu. `CraftBlock['type']` ise sekiz değer tanımlıyor — kalan
 * dördü (subheading, bullet, todo, decision) hiçbir şey çizmiyor, blok
 * görünmez bir boşluk olarak kalıyordu. Başlangıç verisindeki 7 blok bu yüzden
 * ekranda yoktu. Artık her tip burada, tek yerde karşılanıyor ve yeni bir tip
 * eklendiğinde TypeScript eksik dalı yakalıyor.
 */
export const CraftBlockEditor: React.FC<CraftBlockEditorProps> = ({
  block,
  isDarkMode,
  onChange,
  onToggleChecked,
  onDelete
}) => {
  const autoRows = (text: string, min = 2) => Math.max(min, text.split('\n').length);

  const textareaClass = `bg-transparent border-none outline-none w-full resize-none leading-relaxed`;

  const renderBody = () => {
    switch (block.type) {
      case 'heading':
        return (
          <input
            type="text"
            value={block.content}
            onChange={(e) => onChange(block.id, e.target.value)}
            placeholder={t('Başlık', 'Heading')}
            className="text-xl font-bold bg-transparent border-none outline-none w-full text-[#F0EEE6] tracking-tight"
          />
        );

      case 'subheading':
        return (
          <input
            type="text"
            value={block.content}
            onChange={(e) => onChange(block.id, e.target.value)}
            placeholder={t('Alt başlık', 'Subheading')}
            className={`text-[15px] font-semibold bg-transparent border-none outline-none w-full tracking-tight ${
              isDarkMode ? 'text-neutral-200' : 'text-neutral-800'
            }`}
          />
        );

      case 'bullet':
        return (
          <div className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#3AC8E0] shrink-0 mt-2.5"></span>
            <textarea
              value={block.content}
              onChange={(e) => onChange(block.id, e.target.value)}
              rows={autoRows(block.content, 1)}
              placeholder={t('Madde', 'Bullet')}
              className={`${textareaClass} text-[14px] text-neutral-300`}
            />
          </div>
        );

      case 'todo':
        return (
          <div className="flex items-start gap-3">
            <button
              onClick={() => onToggleChecked(block.id)}
              aria-label={block.checked ? t('Tamamlandı olarak işaretlemeyi kaldır', 'Mark as not done') : t('Tamamlandı olarak işaretle', 'Mark as done')}
              className={`w-5 h-5 rounded border flex items-center justify-center transition-colors shrink-0 mt-1 ${
                block.checked
                  ? 'bg-emerald-500 border-emerald-500 text-[#F0EEE6]'
                  : 'border-neutral-500 hover:border-emerald-400'
              }`}
            >
              {block.checked && <Check className="w-3.5 h-3.5 stroke-[3]" />}
            </button>
            <textarea
              value={block.content}
              onChange={(e) => onChange(block.id, e.target.value)}
              rows={autoRows(block.content, 1)}
              placeholder={t('Yapılacak', 'To-do')}
              className={`${textareaClass} text-[14px] ${
                block.checked ? 'line-through text-neutral-500' : 'text-neutral-300'
              }`}
            />
          </div>
        );

      case 'decision':
        return (
          <div className="pl-4 border-l-2 border-amber-400/70 py-1.5">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-amber-400 mb-1">
              <Gavel className="w-3.5 h-3.5" />
              <span>{block.tag || t('Karar', 'Decision')}</span>
            </div>
            <textarea
              value={block.content}
              onChange={(e) => onChange(block.id, e.target.value)}
              rows={autoRows(block.content)}
              className={`${textareaClass} text-[14px] text-neutral-300`}
            />
          </div>
        );

      case 'callout':
        return (
          <div className={`p-5 rounded-lg flex items-start gap-3.5 ${
            isDarkMode ? 'bg-[#19191C] text-neutral-200' : 'bg-neutral-50 text-neutral-800'
          }`}>
            <Lightbulb className="w-4 h-4 text-amber-400 shrink-0 mt-1" />
            <textarea
              value={block.content}
              onChange={(e) => onChange(block.id, e.target.value)}
              rows={autoRows(block.content)}
              className={`${textareaClass} text-[14px]`}
            />
          </div>
        );

      case 'quote':
        return (
          <div className="pl-4 border-l-2 border-[#3AC8E0] py-1.5 italic text-[14px] text-neutral-300">
            <textarea
              value={block.content}
              onChange={(e) => onChange(block.id, e.target.value)}
              rows={autoRows(block.content)}
              className={textareaClass}
            />
          </div>
        );

      case 'paragraph':
        return (
          <textarea
            value={block.content}
            onChange={(e) => onChange(block.id, e.target.value)}
            rows={autoRows(block.content)}
            className={`${textareaClass} text-[14px] text-neutral-300 placeholder:text-neutral-600`}
            placeholder={t('Bir şeyler yazın...', 'Write something…')}
          />
        );

      default: {
        // Tip listesine yeni bir değer eklenip burası unutulursa derleme
        // hatası verir — blok bir daha sessizce kaybolmaz.
        const exhaustive: never = block.type;
        return <span className="text-rose-400 text-[12px]">{t('Bilinmeyen blok', 'Unknown block')}: {String(exhaustive)}</span>;
      }
    }
  };

  return (
    <div className="group relative pr-8">
      {renderBody()}
      <button
        onClick={() => onDelete(block.id)}
        title={t('Bloğu Sil', 'Delete block')}
        aria-label={t('Bloğu sil', 'Delete block')}
        className="absolute right-0 top-1 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-neutral-500 hover:text-rose-400 rounded"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};
